<template>
  <div v-if="isVisible" class="verstaendlichkeit-modal" @click.self="close">
    <div class="verstaendlichkeit-content">
      <h2 class="text-xl font-bold mb-4">Verständlichkeit</h2>
      
      <svg :width="width + 100" :height="height + 100" @click="handleClick">
        <g :transform="`translate(70, 20)`">
          <!-- Raster -->
          <line v-for="(x, index) in xLines" :key="'x'+index" 
                :x1="x" :y1="0" :x2="x" :y2="height" stroke="#ff0000" stroke-width="1" />
          <line v-for="(y, index) in yLines" :key="'y'+index" 
                :x1="0" :y1="y" :x2="width" :y2="y" stroke="#ff0000" stroke-width="1" />
          
          <!-- X-Achse Beschriftung -->
          <text v-for="(label, index) in xLabels" :key="'xlabel'+index"
                :x="xLines[index]" :y="height + 20" text-anchor="middle" font-size="12" fill="black">
            {{ label }}
          </text>
          <text :x="width / 2" :y="height + 40" text-anchor="middle" font-size="14" fill="black">Verständlichkeit in %</text>
          
          <!-- Y-Achse Beschriftung -->
          <text v-for="(label, index) in yLabels" :key="'ylabel'+index"
                x="-10" :y="yLines[index]" text-anchor="end" alignment-baseline="middle" font-size="12" fill="black">
            {{ label }}
          </text>
          <text :transform="yAxisLabelTransform" text-anchor="middle" font-size="14" fill="black">Sprachschallpegel in dB</text>
          
         <!-- Linie -->
         <polyline :points="linePoints" fill="none" stroke="red" stroke-width="2" />
          
          <!-- Punkte -->
          <circle v-for="point in points" :key="point.x+','+point.y"
                  :cx="scaleX(point.x)" :cy="scaleY(point.y)" r="4" fill="red" stroke="white" stroke-width="2" />
        </g>
      </svg>

      <div class="mb-4">
        <label :for="`diskriminationsverlust-${side}`" class="text-sm">Diskriminationsverlust</label>
        <input
          :id="`diskriminationsverlust-${side}`"
          v-model="diskriminationsverlust"
          type="number"
          placeholder="Diskriminationsverlust"
          class="p-2 border rounded w-full"
          @input="updateDiskriminationsverlust"
        />
      </div>

      <button @click="close" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
        Schließen
      </button>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useERezeptStore } from '../../stores/eRezeptStore';

export default {
  props: {
    side: {
      type: String,
      required: true,
      validator: (value) => ['rechts', 'links'].includes(value),
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 600
    },
    height: {
      type: Number,
      default: 400
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const eRezeptStore = useERezeptStore();

    const diskriminationsverlust = ref(eRezeptStore.muster15formData.ohren[props.side].diskriminationsverlust);

    const updateDiskriminationsverlust = () => {
      eRezeptStore.updateMuster15OhrField(props.side, 'diskriminationsverlust', null, diskriminationsverlust.value);
    };

    const xLabels = ['0', '20', '40', '60', '80', '100'];
    const yLabels = ['5', '20', '35', '50', '65', '80', '95', '110'];

    const rawPoints = computed(() => eRezeptStore.getVerstaendlichkeitPoints(props.side));
    const sortedPoints = computed(() => [...rawPoints.value].sort((a, b) => a.x - b.x));

    const points = ref([]);

    watch(sortedPoints, (newPoints) => {
      points.value = newPoints;
    }, { immediate: true });

    const xLines = computed(() => xLabels.map((_, i) => scaleX(i)));
    const yLines = computed(() => yLabels.map((_, i) => scaleY(parseInt(yLabels[i]))));

    const linePoints = computed(() => points.value
      .map(p => `${scaleX(p.x)},${scaleY(p.y)}`)
      .join(' '));

    const scaleX = (x) => x * (props.width / (xLabels.length - 1));
    const scaleY = (y) => props.height - ((y - 5) * props.height / 105);

    const yAxisLabelTransform = computed(() => `rotate(-90) translate(-${props.height / 2}, -50)`);

    const handleClick = (event) => {
      const svg = event.currentTarget;
      const rect = svg.getBoundingClientRect();
      const x = event.clientX - rect.left - 70;
      const y = event.clientY - rect.top - 20;

      if (x < 0 || x > props.width || y < 0 || y > props.height) {
        return;
      }

      const xStep = props.width / (xLabels.length - 1);
      const yStep = props.height / (yLabels.length - 1);

      const xIndex = Math.round(x / xStep);
      const yValue = Math.round((props.height - y) / yStep) * 15 + 5;

      if (yValue >= 5 && yValue <= 110 && xIndex >= 0 && xIndex < xLabels.length) {
        eRezeptStore.updateVerstaendlichkeitPoint(props.side, xIndex, yValue);
      }
    };

    const close = () => {
      emit('close');
    };

    return {
      xLabels,
      yLabels,
      xLines,
      yLines,
      linePoints,
      points,
      handleClick,
      scaleX,
      scaleY,
      close,
      yAxisLabelTransform,
      diskriminationsverlust,
      updateDiskriminationsverlust
    };
  }
};
</script>

<style scoped>
.verstaendlichkeit-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.verstaendlichkeit-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 80%;
  max-height: 80%;
  overflow: auto;
}

.close-button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>